import React, { useMemo, useState } from 'react'
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import {
    ArrowBack as ArrowBackIcon,
    ArrowForwardIos as ArrowForwardIosIcon,
    CheckCircle as CheckCircleIcon,
    EditOutlined as EditOutlinedIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

const ORANGE = '#FF8A00'
const SURFACE = 'rgba(16,11,38,0.92)'
const SURFACE_SOFT = 'rgba(255,255,255,0.03)'
const BORDER = '1px solid rgba(255,255,255,0.08)'
const DIASPORA_CODES = ['US', 'CA', 'UK', 'GB', 'AU', 'FR', 'EU']

const countries = [
    {
        code: 'SL',
        name: 'Sierra Leone',
        flag: 'SL',
        payoutCurrency: 'SLE',
        corridorBlurb: 'Diaspora wallet or Visa funding into local Sierra Leone bank accounts in SLE.',
        processingWindow: 'Instant review, same-day local bank processing',
        banks: [
            'Rokel Commercial Bank',
            'Sierra Leone Commercial Bank',
            'Union Trust Bank',
            'Guaranty Trust Bank',
            'Ecobank Sierra Leone',
        ],
        supportsVisaDirect: true,
    },
    {
        code: 'LR',
        name: 'Liberia',
        flag: 'LR',
        payoutCurrency: 'USD',
        corridorBlurb: 'Liberia bank payouts settle in USD for stronger corridor consistency and easier cash-out.',
        processingWindow: 'Priority USD settlement window within the banking day',
        banks: [
            'Ecobank Liberia',
            'International Bank Liberia',
            'Afriland First Bank Liberia',
            'United Bank for Africa Liberia',
            'Global Bank Liberia',
        ],
        supportsVisaDirect: true,
    },
    {
        code: 'GN',
        name: 'Guinea',
        flag: 'GN',
        payoutCurrency: 'GNF',
        corridorBlurb: 'Send directly into Guinea bank accounts in GNF with local settlement visibility.',
        processingWindow: 'Same-day bank file release for validated beneficiaries',
        banks: [
            'Ecobank Guinea',
            'VistaGui',
            'Societe Generale Guinea',
            'First International Bank Guinea',
            'Banque Populaire Maroco-Guineenne',
        ],
        supportsVisaDirect: false,
    },
    {
        code: 'GM',
        name: 'Gambia',
        flag: 'GM',
        payoutCurrency: 'GMD',
        corridorBlurb: 'Fund Gambian bank deposits in GMD with mobile-money-to-bank switching where needed.',
        processingWindow: 'Fast local bank settlement during operating hours',
        banks: [
            'Trust Bank Gambia',
            'Ecobank Gambia',
            'Access Bank Gambia',
            'Zenith Bank Gambia',
            'AGIB Bank',
        ],
        supportsVisaDirect: false,
    },
]

const ratesToUsd = {
    USD: 1,
    GBP: 1.27,
    CAD: 0.74,
    AUD: 0.66,
    EUR: 1.09,
    SLE: 0.0421,
    GNF: 0.000115,
    GMD: 0.0145,
}

const currencySymbols = {
    USD: '$',
    GBP: '£',
    CAD: 'C$',
    AUD: 'A$',
    EUR: '€',
    SLE: 'SLE ',
    GNF: 'GNF ',
    GMD: 'GMD ',
}

const localCountryMap = {
    'SIERRA LEONE': 'SL',
    SL: 'SL',
    'LIBERIA': 'LR',
    LR: 'LR',
    'GUINEA': 'GN',
    GN: 'GN',
    'GAMBIA': 'GM',
    GM: 'GM',
}

const fieldSx = {
    '& .MuiInputBase-root': { bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 4, color: '#fff' },
    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.62)' },
    '& .MuiInputLabel-root.Mui-focused': { color: ORANGE },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,138,0,0.38)' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ORANGE },
}

const money = (amount, currency) => {
    const numeric = Number(amount || 0)
    const symbol = currencySymbols[currency] || `${currency} `
    if (['USD', 'GBP', 'CAD', 'AUD', 'EUR'].includes(currency)) {
        try {
            return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(numeric)
        } catch {
            return `${currency} ${numeric.toFixed(2)}`
        }
    }
    return `${symbol}${numeric.toFixed(2)}`
}

const row = (label, value, highlight = false) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.62)', fontWeight: 700 }}>{label}</Typography>
        <Typography sx={{ color: highlight ? ORANGE : '#fff', fontWeight: highlight ? 900 : 800, textAlign: 'right' }}>{value}</Typography>
    </Box>
)

const headerCard = (eyebrow, title, subtitle, chips = []) => (
    <Card
        sx={{
            borderRadius: 6,
            mb: 3,
            overflow: 'hidden',
            background: 'linear-gradient(135deg, rgba(20,11,48,0.98) 0%, rgba(77,41,202,0.95) 58%, rgba(255,138,0,0.16) 100%)',
            border: BORDER,
        }}
    >
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
                <Box sx={{ maxWidth: 760 }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontWeight: 700, mb: 1.1 }}>{eyebrow}</Typography>
                    <Typography component="h1" sx={{ color: '#fff', fontSize: { xs: 30, md: 42 }, fontWeight: 950 }}>{title}</Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.80)', mt: 1.2 }}>{subtitle}</Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {chips.map((chip) => (
                        <Chip key={chip} label={chip} sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: '#fff', fontWeight: 800 }} />
                    ))}
                </Stack>
            </Stack>
        </CardContent>
    </Card>
)

export default function BankTransfers() {
    const navigate = useNavigate()
    const savedUser = JSON.parse(localStorage.getItem('session_user') || '{}')
    const isDiaspora = DIASPORA_CODES.includes(String(savedUser?.country || '').toUpperCase())
    const senderCurrency = String(savedUser?.baseCurrency || 'USD').toUpperCase()
    const localCountryCode = localCountryMap[String(savedUser?.country || '').toUpperCase()] || 'SL'
    const defaultCountry = isDiaspora ? null : countries.find((item) => item.code === localCountryCode) || countries[0]

    const [step, setStep] = useState(defaultCountry ? 'banks' : 'corridor')
    const [selectedCountry, setSelectedCountry] = useState(defaultCountry)
    const [selectedBank, setSelectedBank] = useState('')
    const [submittedTransfer, setSubmittedTransfer] = useState(null)
    const [form, setForm] = useState({
        recipientName: '',
        accountNumber: '',
        branchCode: '',
        phone: '',
        sourceMethod: isDiaspora ? 'Wallet Balance' : 'Mobile Money Wallet',
        payoutRail: isDiaspora ? 'Local Bank Deposit' : 'Mobile Money to Bank',
        payoutAmount: '',
    })

    const availableBalance = isDiaspora ? 983.62 : 1000
    const amountInputRef = React.useRef(null)

    const pricing = useMemo(() => {
        const payoutAmount = Number(form.payoutAmount || 0)
        if (!selectedCountry || payoutAmount <= 0) {
            return {
                receiverAmount: 0,
                senderAmount: 0,
                fee: 0,
                totalDebit: 0,
                exchangeRate: 1,
                pricingNote: 'Enter the local amount the bank beneficiary should receive.',
            }
        }

        if (!isDiaspora) {
            const fee = Number(Math.max(payoutAmount * 0.0035, 1).toFixed(2))
            return {
                receiverAmount: payoutAmount,
                senderAmount: payoutAmount,
                fee,
                totalDebit: Number((payoutAmount + fee).toFixed(2)),
                exchangeRate: 1,
                pricingNote: `Local resident pricing keeps bank transfers practical in ${selectedCountry.name}.`,
            }
        }

        const payoutUsdValue = selectedCountry.payoutCurrency === 'USD'
            ? payoutAmount
            : Number((payoutAmount * (ratesToUsd[selectedCountry.payoutCurrency] || 1)).toFixed(4))
        const senderAmount = Number((payoutUsdValue / (ratesToUsd[senderCurrency] || 1)).toFixed(2))
        const fee = Number(Math.max(senderAmount * 0.025, 2.41).toFixed(2))
        const totalDebit = Number((senderAmount + fee).toFixed(2))
        const exchangeRate = payoutAmount > 0 && senderAmount > 0 ? Number((payoutAmount / senderAmount).toFixed(4)) : 1

        return {
            receiverAmount: payoutAmount,
            senderAmount,
            fee,
            totalDebit,
            exchangeRate,
            pricingNote: `${form.sourceMethod} funding with ${form.payoutRail} into ${selectedCountry.name} banking rails. Diaspora remittance pricing applied for this bank transfer using wallet-backed settlement.`,
        }
    }, [form.payoutAmount, form.payoutRail, form.sourceMethod, isDiaspora, selectedCountry, senderCurrency])

    const canContinueDetails = Boolean(form.recipientName.trim()) && Boolean(form.accountNumber.trim()) && Boolean(selectedBank)
    const canReview = Number(form.payoutAmount || 0) > 0 && pricing.totalDebit > 0 && availableBalance >= pricing.totalDebit

    const chooseCountry = (country) => {
        setSelectedCountry(country)
        setSelectedBank('')
        setForm((current) => ({
            ...current,
            payoutAmount: '',
            sourceMethod: isDiaspora ? 'Wallet Balance' : 'Mobile Money Wallet',
            payoutRail: isDiaspora ? 'Local Bank Deposit' : 'Mobile Money to Bank',
        }))
        setStep('banks')
    }

    const confirmTransfer = () => {
        if (!selectedCountry || !selectedBank) return
        setSubmittedTransfer({
            reference: `BTX${Date.now()}`,
            country: selectedCountry.name,
            bank: selectedBank,
            recipientName: form.recipientName,
            payoutCurrency: selectedCountry.payoutCurrency,
            senderCurrency,
            processingWindow: selectedCountry.processingWindow,
            receiverAmount: pricing.receiverAmount,
            totalDebit: pricing.totalDebit,
            status: 'PENDING',
        })
        setStep('success')
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#09061B', py: { xs: 4, md: 6 } }}>
            <Container maxWidth="md">
                {step === 'corridor' && headerCard(
                    'Bank Transfer Corridor',
                    'Bank Transfer Corridor',
                    `Resident detected: ${savedUser?.country || 'United States'} ${isDiaspora ? 'diaspora' : 'resident'}. Choose the African market where the beneficiary bank will receive funds.`,
                    isDiaspora ? ['Cross-border ready', senderCurrency] : ['Local resident', senderCurrency]
                )}
                {step === 'banks' && selectedCountry && headerCard(
                    `Banks in ${selectedCountry.name}`,
                    `Banks in ${selectedCountry.name}`,
                    selectedCountry.processingWindow,
                    [`Settlement in ${selectedCountry.payoutCurrency}`]
                )}
                {step === 'details' && selectedCountry && selectedBank && headerCard(
                    selectedBank,
                    selectedBank,
                    `${selectedCountry.corridorBlurb} ${isDiaspora ? 'Diaspora detected from your resident profile. This bank transfer will behave like remittance and fund from wallet balance or Visa/debit rail.' : 'Local resident profile detected. This bank transfer will use resident-local funding logic.'}`,
                    [`Destination: ${selectedCountry.name}`, `Payout currency: ${selectedCountry.payoutCurrency}`]
                )}
                {step === 'amount' && headerCard('Payout Amount', 'Payout Amount', 'Enter the local amount the bank beneficiary should receive.')}
                {step === 'review' && headerCard('Review Transfer', 'Review Transfer', 'Confirm beneficiary, corridor, and pricing details before submitting the bank transfer.')}

                {step === 'success' && submittedTransfer && (
                    <Card sx={{ borderRadius: 6, border: BORDER, bgcolor: SURFACE }}>
                        <CardContent sx={{ p: { xs: 3, md: 4 }, textAlign: 'center' }}>
                            <CheckCircleIcon sx={{ fontSize: 72, color: ORANGE, mb: 1.2 }} />
                            <Typography sx={{ color: '#fff', fontWeight: 950, fontSize: 36 }}>Transfer Submitted</Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.68)', mt: 1 }}>{submittedTransfer.country} • {submittedTransfer.bank}</Typography>
                            <Card sx={{ mt: 3, borderRadius: 5, bgcolor: SURFACE_SOFT, border: BORDER, textAlign: 'left' }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Stack spacing={1.3}>
                                        {row('Reference', submittedTransfer.reference)}
                                        {row('Recipient', submittedTransfer.recipientName)}
                                        {row('Bank Credit', money(submittedTransfer.receiverAmount, submittedTransfer.payoutCurrency))}
                                        {row('Total Debit', money(submittedTransfer.totalDebit, submittedTransfer.senderCurrency))}
                                        {row('Status', submittedTransfer.status)}
                                        {row('Settlement', submittedTransfer.processingWindow)}
                                    </Stack>
                                </CardContent>
                            </Card>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3 }}>
                                <Button fullWidth variant="outlined" onClick={() => navigate('/transactions')} sx={{ borderRadius: 999, py: 1.2, textTransform: 'none', fontWeight: 800, color: '#fff', borderColor: 'rgba(255,255,255,0.16)' }}>View History</Button>
                                <Button fullWidth variant="contained" onClick={() => navigate('/dashboard')} sx={{ borderRadius: 999, py: 1.2, textTransform: 'none', fontWeight: 900, bgcolor: ORANGE, '&:hover': { bgcolor: '#ff9c2a' } }}>Back Home</Button>
                            </Stack>
                        </CardContent>
                    </Card>
                )}

                {step === 'corridor' && (
                    <Stack spacing={2}>
                        {countries.map((country) => (
                            <Card key={country.code} onClick={() => chooseCountry(country)} sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER, cursor: 'pointer', '&:hover': { borderColor: 'rgba(255,138,0,0.30)' } }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Box sx={{ width: 54, height: 54, borderRadius: 3, bgcolor: 'rgba(91,46,255,0.18)', color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 900 }}>{country.flag}</Box>
                                            <Box>
                                                <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 20 }}>{country.name}</Typography>
                                                <Typography sx={{ color: ORANGE, fontWeight: 800, mt: 0.4 }}>Payout currency: {country.payoutCurrency}</Typography>
                                                <Typography sx={{ color: 'rgba(255,255,255,0.70)', mt: 0.8 }}>{country.corridorBlurb}</Typography>
                                            </Box>
                                        </Stack>
                                        <ArrowForwardIosIcon sx={{ color: ORANGE, fontSize: 18 }} />
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                )}

                {step === 'banks' && selectedCountry && (
                    <Stack spacing={2}>
                        {!defaultCountry && (
                            <Button startIcon={<ArrowBackIcon />} onClick={() => setStep('corridor')} sx={{ alignSelf: 'flex-start', color: '#fff', textTransform: 'none', fontWeight: 700 }}>Back</Button>
                        )}
                        <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography sx={{ color: ORANGE, fontWeight: 900, fontSize: 22 }}>Settlement in {selectedCountry.payoutCurrency}</Typography>
                                <Typography sx={{ color: 'rgba(255,255,255,0.72)', mt: 1 }}>{selectedCountry.processingWindow}</Typography>
                            </CardContent>
                        </Card>
                        {selectedCountry.banks.map((bank) => (
                            <Card key={bank} onClick={() => { setSelectedBank(bank); setStep('details') }} sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER, cursor: 'pointer', '&:hover': { borderColor: 'rgba(255,138,0,0.30)' } }}>
                                <CardContent sx={{ p: 2.6 }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 18 }}>{bank}</Typography>
                                        <ArrowForwardIosIcon sx={{ color: ORANGE, fontSize: 18 }} />
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                )}

                {step === 'details' && selectedCountry && selectedBank && (
                    <Stack spacing={3}>
                        <Button startIcon={<ArrowBackIcon />} onClick={() => setStep('banks')} sx={{ alignSelf: 'flex-start', color: '#fff', textTransform: 'none', fontWeight: 700 }}>Back</Button>
                        <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}>
                            <CardContent sx={{ p: 3 }}>
                                <Stack spacing={2}>
                                    <TextField fullWidth label="Account Holder Name" value={form.recipientName} onChange={(e) => setForm((c) => ({ ...c, recipientName: e.target.value }))} sx={fieldSx} />
                                    <TextField fullWidth label="Account Number / IBAN" value={form.accountNumber} onChange={(e) => setForm((c) => ({ ...c, accountNumber: e.target.value }))} sx={fieldSx} />
                                    <TextField fullWidth label="Branch / Routing Code (optional)" value={form.branchCode} onChange={(e) => setForm((c) => ({ ...c, branchCode: e.target.value }))} sx={fieldSx} />
                                    <TextField fullWidth label="Recipient Phone (optional)" value={form.phone} onChange={(e) => setForm((c) => ({ ...c, phone: e.target.value }))} sx={fieldSx} />
                                    <TextField select fullWidth label="Source Method" value={form.sourceMethod} onChange={(e) => setForm((c) => ({ ...c, sourceMethod: e.target.value }))} sx={fieldSx}>
                                        {(isDiaspora ? ['Wallet Balance', 'Visa / Debit Card'] : ['Mobile Money Wallet', 'Wallet Balance']).map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                                    </TextField>
                                    <TextField select fullWidth label="Payout Rail" value={form.payoutRail} onChange={(e) => setForm((c) => ({ ...c, payoutRail: e.target.value }))} sx={fieldSx}>
                                        {(isDiaspora ? ['Local Bank Deposit', ...(selectedCountry.supportsVisaDirect ? ['Visa Direct'] : [])] : ['Local Bank Deposit', 'Mobile Money to Bank']).map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                                    </TextField>
                                    <Button variant="contained" disabled={!canContinueDetails} onClick={() => setStep('amount')} sx={{ borderRadius: 999, py: 1.35, textTransform: 'none', fontWeight: 900, bgcolor: ORANGE, '&:hover': { bgcolor: '#ff9c2a' } }}>Continue</Button>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Stack>
                )}

                {step === 'amount' && selectedCountry && selectedBank && (
                    <Stack spacing={3}>
                        <Button startIcon={<ArrowBackIcon />} onClick={() => setStep('details')} sx={{ alignSelf: 'flex-start', color: '#fff', textTransform: 'none', fontWeight: 700 }}>Back</Button>
                        <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}>
                            <CardContent sx={{ p: 3 }}>
                                <Stack spacing={2.2}>
                                    <Card sx={{ borderRadius: 5, bgcolor: SURFACE_SOFT, border: BORDER }}>
                                        <CardContent sx={{ p: 3 }}>
                                            <Typography sx={{ color: 'rgba(255,255,255,0.62)', fontWeight: 700 }}>Recipient should receive</Typography>
                                            <Box
                                                onClick={() => amountInputRef.current?.focus()}
                                                sx={{
                                                    mt: 1.2,
                                                    px: 2,
                                                    py: 1.6,
                                                    borderRadius: 3,
                                                    bgcolor: 'rgba(255,255,255,0.04)',
                                                    border: '1px solid rgba(255,255,255,0.12)',
                                                    cursor: 'text',
                                                    transition: 'border-color 0.18s ease, box-shadow 0.18s ease',
                                                    '&:hover': {
                                                        borderColor: 'rgba(255,138,0,0.42)',
                                                    },
                                                    '&:focus-within': {
                                                        borderColor: ORANGE,
                                                        boxShadow: '0 0 0 3px rgba(255,138,0,0.14)',
                                                    },
                                                }}
                                            >
                                                <Stack direction="row" spacing={1.2} alignItems="center">
                                                    <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 28 }}>
                                                        {selectedCountry.payoutCurrency}
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        inputRef={amountInputRef}
                                                        variant="standard"
                                                        autoFocus
                                                        placeholder="0.00"
                                                        value={form.payoutAmount}
                                                        onChange={(e) => setForm((c) => ({ ...c, payoutAmount: e.target.value }))}
                                                        InputProps={{
                                                            disableUnderline: true,
                                                            startAdornment: !form.payoutAmount ? (
                                                                <EditOutlinedIcon sx={{ color: 'rgba(255,255,255,0.38)', mr: 1 }} />
                                                            ) : null,
                                                            sx: {
                                                                color: '#fff',
                                                                fontWeight: 900,
                                                                fontSize: 34,
                                                                '& input::placeholder': {
                                                                    color: 'rgba(255,255,255,0.28)',
                                                                    opacity: 1,
                                                                },
                                                            },
                                                        }}
                                                    />
                                                </Stack>
                                            </Box>
                                            <Typography sx={{ color: 'rgba(255,255,255,0.58)', mt: 1.2 }}>Enter the local amount the bank beneficiary should receive.</Typography>
                                        </CardContent>
                                    </Card>
                                    <Card sx={{ borderRadius: 5, bgcolor: SURFACE_SOFT, border: BORDER }}>
                                        <CardContent sx={{ p: 3 }}>
                                            <Stack spacing={1.3}>
                                                {row('Source Method', form.sourceMethod)}
                                                {row('Payout Rail', form.payoutRail)}
                                                {row('Bank', selectedBank)}
                                                {row('Available Wallet Balance', money(availableBalance, senderCurrency))}
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                    {Number(form.payoutAmount || 0) > 0 && (
                                        <Card sx={{ borderRadius: 5, bgcolor: SURFACE_SOFT, border: BORDER }}>
                                            <CardContent sx={{ p: 3 }}>
                                                <Stack spacing={1.25}>
                                                    {row('Recipient gets', money(pricing.receiverAmount, selectedCountry.payoutCurrency))}
                                                    {row('You send', money(pricing.senderAmount, senderCurrency))}
                                                    {row('Fee', money(pricing.fee, senderCurrency))}
                                                    {row('FX rate', `1 ${senderCurrency} = ${pricing.exchangeRate.toFixed(4)} ${selectedCountry.payoutCurrency}`)}
                                                    <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.10)', pt: 1.5 }}>
                                                        {row('Total charged', money(pricing.totalDebit, senderCurrency), true)}
                                                    </Box>
                                                    <Typography sx={{ color: 'rgba(255,255,255,0.62)', fontSize: 13.5, lineHeight: 1.55 }}>{pricing.pricingNote}</Typography>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    )}
                                    {!canReview && Number(form.payoutAmount || 0) > 0 && pricing.totalDebit > availableBalance && (
                                        <Typography sx={{ color: '#ff7d7d', fontWeight: 700 }}>Insufficient wallet balance for this bank transfer.</Typography>
                                    )}
                                    <Button variant="contained" disabled={!canReview} onClick={() => setStep('review')} sx={{ borderRadius: 999, py: 1.35, textTransform: 'none', fontWeight: 900, bgcolor: ORANGE, '&:hover': { bgcolor: '#ff9c2a' } }}>Review Transfer</Button>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Stack>
                )}

                {step === 'review' && selectedCountry && selectedBank && (
                    <Stack spacing={3}>
                        <Button startIcon={<ArrowBackIcon />} onClick={() => setStep('amount')} sx={{ alignSelf: 'flex-start', color: '#fff', textTransform: 'none', fontWeight: 700 }}>Back</Button>
                        <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 20, mb: 2 }}>Beneficiary</Typography>
                                <Stack spacing={1.3}>
                                    {row('Recipient', form.recipientName)}
                                    {row('Country', selectedCountry.name)}
                                    {row('Bank', selectedBank)}
                                    {row('Account', `****${form.accountNumber.slice(-4)}`)}
                                    {row('Phone', form.phone || '—')}
                                </Stack>
                            </CardContent>
                        </Card>
                        <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 20, mb: 2 }}>Corridor</Typography>
                                <Stack spacing={1.3}>
                                    {row('Source Method', form.sourceMethod)}
                                    {row('Payout Rail', form.payoutRail)}
                                    {row('Settlement Window', selectedCountry.processingWindow)}
                                    {row('Pricing Note', pricing.pricingNote)}
                                </Stack>
                            </CardContent>
                        </Card>
                        <Card sx={{ borderRadius: 5, bgcolor: SURFACE, border: BORDER }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 20, mb: 2 }}>Transfer Summary</Typography>
                                <Stack spacing={1.3}>
                                    {row('Recipient gets', money(pricing.receiverAmount, selectedCountry.payoutCurrency))}
                                    {row('You send', money(pricing.senderAmount, senderCurrency))}
                                    {row('Fee', money(pricing.fee, senderCurrency))}
                                    {row('FX rate', `1 ${senderCurrency} = ${pricing.exchangeRate.toFixed(4)} ${selectedCountry.payoutCurrency}`)}
                                    <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.10)', pt: 1.5 }}>
                                        {row('Total charged', money(pricing.totalDebit, senderCurrency), true)}
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                        <Button variant="contained" onClick={confirmTransfer} sx={{ borderRadius: 999, py: 1.35, textTransform: 'none', fontWeight: 900, bgcolor: ORANGE, '&:hover': { bgcolor: '#ff9c2a' } }}>Confirm Transfer</Button>
                    </Stack>
                )}
            </Container>
        </Box>
    )
}
